import React, { useEffect } from "react";
import { AppState, useAppDispatch, useAppSelector } from "../../store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  setIsEditStudy,
  setOpenAddStudyModal,
} from "../../store/slices/studiesSlice";
import DynamicForm from "../common/DynamicForm";
import useAddStudy from "./useAddStudy";
import { AlertDialog } from "../common/AlertDailog";
import { studySchema } from "../../config/schemas/studySchema";
import { useStudies } from "../../hooks/useStudies";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useCodelist } from "../../hooks/useCodelist";

const AddStudyModal: React.FunctionComponent<any> = () => {
  const dispatch = useAppDispatch();
  const { showSuccess, handleError } = useSnackbar();
  const { getStudyDetails } =
    useStudies();
  const selectStudyData = (state: AppState) => ({
    selectedStudy: state.studiesSlice.selectedStudy,
    isEdit: state.studiesSlice.isEditStudy,
    studyData: state.studiesSlice.studyDetails,
    openAddStudyModal: state.studiesSlice.openAddStudyModal,
    armsError: state.studiesSlice.armsError,
  });
  const { selectedStudy, isEdit, studyData, openAddStudyModal, armsError } =
    useAppSelector(selectStudyData);

  type FormValues = z.infer<typeof studySchema>;

  const codes = ["STUDY_TYPE", "STUDY_PHASE", "ARM_TYPE", "RECRUITMENT_STATUS"];

  const { getCodelistData, codelistData } = useCodelist();

  // const [initialValues,setInitialValues] = useState<any>()

  useEffect(() => {
    getCodelistData({ data: { code_list_codes: codes } });
  }, []);



  const initialValues = isEdit 
  ? {
          ...studyData,
          ...studyData?.study_details,
          study_phase: {
            code_list_value: studyData.study_details?.study_phase,
            code_list_value_code:
              studyData?.study_details?.study_phase_value_code,
          },
          study_type: {
          code_list_value: studyData.study_details?.study_type,
            code_list_value_code: studyData?.study_details?.study_type_value_code,
          },
          study_status: {
            code_list_value: studyData.study_details?.study_status,
            code_list_value_code:
              studyData?.study_details?.study_status_value_code,
          },
        }
    : {};

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    unregister,
    watch,
    setValue,
    setFocus,
  } = useForm<FormValues>({
    resolver: zodResolver(studySchema),
    defaultValues: initialValues,
  });
  const { addStudyFields, editStudyFields } = useAddStudy({
    register,
    unregister,
    errors,
    watch,
    setValue,
    studyData,
    isEdit,
    codelistData
  });
  // const [fields, setFields] = useState(
  //   isEdit ? editStudyFields : addStudyFields
  // );

  useEffect(() => {
    const firstError = Object.keys(errors).reduce((field: any, a) => {
      return (errors as any)[field] ? field : a;
    }, null);
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  // const watchStudyType = watch("study_type");
  // useEffect(() => {
  //   if (
  //     watchStudyType &&
  //     watchStudyType["code_list_value"] == "Interventional Study"
  //   ) {
  //     const temp = addStudyFields.map((item: any) => {
  //       if (item.name == "study_arms") {
  //         return { ...item, visibility: "true" };
  //       } else {
  //         return { ...item };
  //       }
  //     });
  //     setFields(temp);
  //   } else {
  //     setFields(isEdit ? editStudyFields : addStudyFields);
  //   }
  // }, [watchStudyType, watch("treatments"), watch("conditions")]);

  const onInvalid = (errors: any) => console.debug(errors);

  const onCloseAddStudyModal = () => {
    dispatch(setOpenAddStudyModal(false));
    dispatch(setIsEditStudy(false));
  };

  const {
    createStudy,
    createStudyData,
    createStudyIsLoading,
    createStudyIsSuccess,
    createStudyIsError,
    createStudyError,
    updateStudy,
    updateStudyData,
    updateStudyIsLoading,
    updateStudyIsSuccess,
    updateStudyError,
    updateStudyIsError,
  } = useStudies();

  const submit = (data: any) => {
    const temp = {
      ...data,
      study_type: data.study_type.code_list_value,
      study_type_code: "STUDY_TYPE",
      study_type_value_code: data.study_type.code_list_value_code,
      study_phase: data.study_phase.code_list_value,
      study_phase_code: "STUDY_PHASE",
      study_phase_value_code: data.study_phase.code_list_value_code,
      study_status: data.study_status.code_list_value,
      study_status_code: "RECRUITMENT_STATUS",
      study_status_value_code: data.study_status.code_list_value_code,
      primary_disease_or_conditions: data.conditions,
    };
    delete temp.conditions;
    if (armsError) {
      return;
    }
    isEdit ? updateStudy({ data: temp }) : createStudy({ data: temp });
  };

  useEffect(() => {
    if (!createStudyIsLoading && createStudyIsSuccess) {
      const successMsg = createStudyData;
      showSuccess(successMsg?.details, "study-create-success");
      onCloseAddStudyModal();
    }
  }, [createStudyData]);

  useEffect(() => {
    if (createStudyIsError && createStudyError) {
      const errorMsg: any = createStudyError;
      handleError(errorMsg);
    }
  }, [createStudyIsError, createStudyError]);

  useEffect(() => {
    if (!updateStudyIsLoading && updateStudyIsSuccess) {
      const successMsg = updateStudyData;
      getStudyDetails({ study_id: selectedStudy });
      showSuccess(successMsg?.details, "study-update-success");
      onCloseAddStudyModal();
    }
  }, [updateStudyData]);

  useEffect(() => {
    if (updateStudyIsError && updateStudyError) {
      const errorMsg: any = updateStudyError;
      handleError(errorMsg);
    }
  }, [updateStudyIsError, updateStudyError]);

  return (
    <AlertDialog
      open={openAddStudyModal}
      title="Study Details"
      handleClose={onCloseAddStudyModal}
      handleConfirm={handleSubmit(submit, onInvalid)}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit(submit, onInvalid)}>
        <DynamicForm
          fieldsJson={isEdit ? editStudyFields : addStudyFields}
          errors={errors}
          register={register}
          control={control}
        />
      </form>
    </AlertDialog>
  );
};

export default AddStudyModal;
